import { Card, Row, Col, Typography, Empty, Pagination, theme } from "antd";
import { ContributorsList } from "src/components/ContributorsList";
import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "../../useEntityContext";
import { useEntityContributorsContext } from "../../useEntityContributors";
import { StandardCard } from "./StandardCard";
import { useState } from "react";

const { Title } = Typography;

interface Contributor {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  role?: string;
}

export interface EntityContributorsProps {
  entity: any;
  showTitle?: boolean;
  showList?: boolean;
  showRoles?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const EntityContributors: React.FC<EntityContributorsProps> = ({
  entity,
  showTitle = true,
  showList = true,
  showRoles = false,
  className,
  style,
}) => {
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { query } = useEntityContributorsContext();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  if (query.isLoading) {
    return (
      <StandardCard className={className} style={style}>
        <div style={{ textAlign: "center", padding: "12px" }}>
          Loading contributors...
        </div>
      </StandardCard>
    );
  }

  if (query.error || !query.data?.data) {
    return (
      <StandardCard className={className} style={style}>
        <Empty description="No contributors found" />
      </StandardCard>
    );
  }

  const contributors = query.data.data.map((item: any) => ({
    id: item.actor.properties.uuid,
    name: item.actor.properties.name,
    email: item.actor.properties.email,
    avatarUrl: item.actor.properties.avatarUrl,
    role: item.relations[0], // Using the first relation as the role
  }));

  if (contributors.length === 0) {
    return (
      <StandardCard className={className} style={style}>
        <Empty description="No contributors found" />
      </StandardCard>
    );
  }

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentContributors = contributors.slice(startIndex, endIndex);

  return (
    <StandardCard
      className={className}
      style={style}
      headerStyle={{
        padding: "16px 24px",
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        marginBottom: 0,
      }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: 500,
              color: token.colorText,
            }}
          >
            Contributors
          </Title>
          <Pagination
            size="small"
            total={contributors.length}
            pageSize={pageSize}
            current={currentPage}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(total, range) => (
              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                showing {range[0]}-{range[1]} of {total}
              </span>
            )}
          />
        </div>
      }
      bodyStyle={{
        padding: "16px 24px",
      }}
    >
      {showList && contributors.length > 0 && (
        <ContributorsList
          users={currentContributors.map((c: Contributor) => ({
            ...c,
            contribution: `${c.role || "Contributor"}`,
          }))}
        />
      )}
    </StandardCard>
  );
};
